import { ImageWithFallback } from "@/components/ImageWithFallback";
import { PointsPrice } from "@/components/organisms/SBCs/PointsPrice/PointsPrice";
import { useTranslation } from "@repo/shared/i18n";

type Props = {
  imageURL: string;
  name: string;
  /** Already formatted for display. */
  price: string;
  /** A Streamline set's item-score target, printed under the coin price. */
  pointsRequired?: number;
  /** New / Refreshed marker, printed beside the name. */
  badge?: React.ReactNode;
  /** Sits past the price, at the corner: the card's own menu. */
  trailing?: React.ReactNode;
};

/**
 * Crest, name and what the thing costs — the top row of both the challenge
 * tile and the set card. Oswald is for figures only; the name is body face.
 */
export const CardHeader = ({
  imageURL,
  name,
  price,
  pointsRequired,
  badge,
  trailing,
}: Props) => {
  const { t } = useTranslation("common");

  return (
    <div className="flex items-start gap-x-3.5 px-5 pb-3 pt-5">
      <ImageWithFallback
        loading="lazy"
        /* The crest PNGs are drawn with a margin of their own, which reads
           as a gap the layout did not ask for. */
        className="-m-1 size-12 shrink-0 object-contain"
        src={imageURL}
        alt={name}
        fallback={
          import.meta.env.VITE_APP_ASSETS_BASE_URL + "/misc/default-sbc.png"
        }
      />
      {/* Badge beside the name, not floated inside it: Safari's line-clamp
          ignores floats and put the badge on a line of its own. */}
      <div className="flex min-w-0 flex-1 items-start gap-x-1.5 pt-0.5">
        <h3 className="line-clamp-2 min-w-0 flex-1 text-base font-semibold leading-tight text-foreground mobile:text-lg">
          {name}
        </h3>
        {badge && <span className="shrink-0 pt-0.5">{badge}</span>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-y-1">
        <span className="flex items-center gap-x-1.5 font-oswald text-lg font-medium leading-none text-foreground mobile:text-2xl">
          <img
            className="size-[18px]"
            src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`}
            alt={t("alt.coin")}
          />
          {price}
        </span>
        {pointsRequired != null && (
          <PointsPrice
            points={pointsRequired}
            size={12}
            className="text-xs text-foreground-muted"
          />
        )}
      </div>
      {trailing}
    </div>
  );
};
